import type {
  IGetCampaignsParameters,
  IGetCategoriesParameters,
  ITransformResponseName,
  ITransformResponseTitle,
  IUserConfig,
} from '@api/types';
import type { IOption } from '@shared/form/types';

import { apiSlice } from '@api/apiSlice';
import { catchCommonError } from '@utils/catchCommonError';
import { responseToSelectOptions } from '@utils/responseFormatters';

import { API } from '@constants';

export const commonApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserConfig: builder.query<IUserConfig, unknown>({
      serializeQueryArgs: () => 'getUserConfig',
      query: () => ({
        url: API.COMMON.USER,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),

    getUsers: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getUsers',
      query: () => ({
        url: API.COMMON.USERS,
      }),
      transformResponse: (response: ITransformResponseTitle[]) =>
        responseToSelectOptions(response, {
          key: 'name',
        }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),

    getBrands: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getBrands',
      query: () => ({
        url: API.COMMON.BRANDS,
      }),
      transformResponse: (response: ITransformResponseTitle[]) =>
        responseToSelectOptions(response, {
          key: 'title',
        }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),

    getCampaigns: builder.query<IOption[], IGetCampaignsParameters>({
      query: (parameters) => ({
        params: parameters,
        url: API.COMMON.CAMPAIGNS,
      }),
      transformResponse: (response: ITransformResponseName[]) =>
        responseToSelectOptions(response, {
          key: 'name',
        }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),

    getAllBonusTemplates: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getAllBonusTemplates',
      query: () => ({
        url: API.COMMON.BONUS_TEMPLATES,
      }),
      transformResponse: (response: ITransformResponseName[]) =>
        responseToSelectOptions(response, {
          key: 'name',
        }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),

    getCategories: builder.query<IOption[], IGetCategoriesParameters>({
      transformResponse: (response: ITransformResponseName[]) =>
        responseToSelectOptions(response, {
          key: 'name',
        }),
      query: ({ brandIds, fullMatch }) => ({
        url: API.COMMON.CATEGORIES,
        params: { fullMatch, brandIds: brandIds.join(',') },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetBrandsQuery,
  useGetCampaignsQuery,
  useGetCategoriesQuery,
  useGetAllBonusTemplatesQuery,
} = commonApiSlice;
